class TimelineHistory extends HTMLElement {
  constructor() {
    super();
    this._loop = this._loop.bind(this);
    this._onScroll = this._onScroll.bind(this);
    this._onTouchStart = this._onTouchStart.bind(this);
    this._onTouchMove = this._onTouchMove.bind(this);
    this._onTouchEnd = this._onTouchEnd.bind(this);
    this._onWindowResize = this._onWindowResize.bind(this);
    this._recomputeMetrics = this._recomputeMetrics.bind(this);

    this.lastProgress = 0;
    this.targetProgress = 0;
    this.parallaxSpeed = 180;
    this.maxTranslate = 1;
    this.isMobile = null;

    this.touchActive = false;
    this.touchStartY = 0;
    this.touchDelta = 0;
  }

  connectedCallback() {
    requestAnimationFrame(() => this.init());
  }

  init() {
    this.section = this.querySelector(".timeline-history-section");
    if (!this.section) return;

    const isMobile = window.matchMedia("(max-width: 767px)").matches;
    this.isMobile = isMobile;
    this.parallaxSpeed = isMobile ? 340 : 180;

    this.blocks = Array.from(
      this.section.querySelectorAll(
        isMobile ? ".timeline-history-mobile" : ".timeline-history-desktop"
      )
    );
    if (this.blocks.length === 0) return;

    if (!this.wrapper) {
      this.wrapper = document.createElement("div");
      this.wrapper.classList.add("timeline-wrapper");
      this.section.appendChild(this.wrapper);
    }
    this.blocks.forEach((b) => this.wrapper.appendChild(b));

    if (!this.scrollArea) {
      this.scrollArea = document.createElement("div");
      this.scrollArea.className = "timeline-history-scroll-area";
      this.scrollArea.style.width = "1px";
      this.scrollArea.style.opacity = "0";
      this.scrollArea.style.pointerEvents = "none";
      this.parentNode.insertBefore(this.scrollArea, this.nextSibling);
    }

    // sticky section setup
    Object.assign(this.section.style, {
      position: "sticky",
      top: "0",
      overflow: "hidden",
      height: "100vh",
      zIndex: "5",
      backgroundPosition: "center center",
      willChange: "transform, background-position",
      transform: "translate3d(0,0,0)",
    });

    // Set the scroll distance right away so scrolling is never blocked.
    // Some images (especially in later blocks) may still be lazy-
    // loading — the ResizeObserver below grows the reserved scroll
    // distance live as each block's real height changes, so nothing
    // gets cropped without ever having to wait for anything upfront.
    this._recomputeMetrics();

    if (!this._resizeObserver) {
      this._resizeObserver = new ResizeObserver(() => {
        cancelAnimationFrame(this._metricsRaf);
        this._metricsRaf = requestAnimationFrame(this._recomputeMetrics);
      });
    }
    this._resizeObserver.disconnect();
    this.blocks.forEach((b) => this._resizeObserver.observe(b));

    if (!this._raf) this._raf = requestAnimationFrame(this._loop);

    if (!this._scrollListenerAttached) {
      window.addEventListener("scroll", this._onScroll, { passive: true });
      window.addEventListener("resize", this._onWindowResize);
      this._scrollListenerAttached = true;
    }

    if (isMobile && !this._touchListenersAttached) {
      this.section.addEventListener("touchstart", this._onTouchStart, { passive: true });
      this.section.addEventListener("touchmove", this._onTouchMove, { passive: true });
      this.section.addEventListener("touchend", this._onTouchEnd, { passive: true });
      this._touchListenersAttached = true;
    }
  }

  _recomputeMetrics() {
    if (!this.section || !this.scrollArea || !this.blocks || this.blocks.length === 0) return;

    this.blockHeights = this.blocks.map((b) =>
      Math.max(b.offsetHeight, window.innerHeight)
    );
    const totalWrapperHeight = this.blockHeights.reduce((sum, h) => sum + h, 0);
    this.maxTranslate = Math.max(totalWrapperHeight - window.innerHeight, 1);

    const buffer = window.innerHeight * 0.7;
    this.scrollArea.style.height = `${this.maxTranslate + this.section.offsetHeight + buffer}px`;
    this.scrollArea.style.marginTop = `-${this.section.offsetHeight}px`;

    this.targetProgress = Math.min(Math.max(this.targetProgress, 0), 1);
  }

  _onTouchStart(e) {
    this.touchActive = true;
    this.touchStartY = e.touches[0].clientY;
    this.touchDelta = 0;
  }

  _onTouchMove(e) {
    if (!this.touchActive) return;
    const currentY = e.touches[0].clientY;
    this.touchDelta = this.touchStartY - currentY;

    this.targetProgress += (this.touchDelta * 1.5) / this.maxTranslate;
    this.targetProgress = Math.min(Math.max(this.targetProgress, 0), 1);

    this.touchStartY = currentY;
  }

  _onTouchEnd() {
    this.touchActive = false;
  }

  _onScroll() {
    const rect = this.scrollArea.getBoundingClientRect();
    const max = Math.max(rect.height - window.innerHeight, 1);
    const raw = -rect.top;
    this.targetProgress = Math.min(Math.max(raw / max, 0), 1);
  }

  _onWindowResize() {
    // A plain viewport resize just needs new measurements. Crossing the
    // mobile/desktop breakpoint swaps which set of blocks is active, so
    // that case needs a full re-init.
    const isMobile = window.matchMedia("(max-width: 767px)").matches;
    if (isMobile !== this.isMobile) {
      this.init();
    } else {
      this._recomputeMetrics();
    }
  }

  _loop() {
    if (!this.scrollArea || !this.section || !this.wrapper) {
      this._raf = requestAnimationFrame(this._loop);
      return;
    }

    this.lastProgress = this.targetProgress;

    const translateY = -this.lastProgress * this.maxTranslate;
    this.wrapper.style.transform = `translate3d(0, ${translateY}px, 0)`;

    const bgShift = -(this.lastProgress * this.parallaxSpeed);
    this.section.style.backgroundPosition = `center calc(50% + ${bgShift}px)`;

    this._raf = requestAnimationFrame(this._loop);
  }

  disconnectedCallback() {
    cancelAnimationFrame(this._raf);
    cancelAnimationFrame(this._metricsRaf);
    if (this._resizeObserver) this._resizeObserver.disconnect();
    window.removeEventListener("scroll", this._onScroll);
    window.removeEventListener("resize", this._onWindowResize);
  }
}

if (!customElements.get("timeline-history")) {
  customElements.define("timeline-history", TimelineHistory);
}